import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { 
  WebSocketMessage, 
  AudioMessage, 
  TranscriptionMessage, 
  ErrorWebSocketMessage, 
  StatusMessage, 
  ConfigMessage,
  ErrorMessage,
  StatusUpdate,
  createConfigMessage
} from '../types/websocket';
import { AudioChunk } from '../types/audio';
import { TranscriptionResult } from '../types/transcription';
import { LanguageOption } from '../types/language';
import { AudioEncoder } from '../services/AudioEncoder';
import { WebSocketErrorHandler } from '../services/WebSocketErrorHandler';

export interface WebSocketState {
  /** Current connection status */
  connectionStatus: 'disconnected' | 'connecting' | 'connected' | 'error';
  /** Whether the WebSocket is ready to send messages */
  isReady: boolean;
  /** Current error message, if any */
  error: string | null;
  /** Last received status update */
  lastStatus: StatusUpdate | null;
  /** Session ID if available */
  sessionId: string | null;
}

export interface WebSocketCallbacks {
  /** Called when a transcription result is received */
  onTranscription?: (result: TranscriptionResult) => void;
  /** Called when an error message is received */
  onError?: (error: ErrorMessage) => void;
  /** Called when a status update is received */
  onStatusUpdate?: (status: StatusUpdate) => void;
  /** Called when connection state changes */
  onConnectionChange?: (status: WebSocketState['connectionStatus']) => void;
}

export interface WebSocketActions {
  /** Connect to the WebSocket server */
  connect: (url: string) => void;
  /** Disconnect from the WebSocket server */
  disconnect: () => void;
  /** Send audio chunk to the server */
  sendAudioChunk: (chunk: AudioChunk) => boolean;
  /** Send session configuration */
  sendConfig: (language: LanguageOption, audioFormat?: string, sampleRate?: number) => boolean;
  /** Send a generic message */
  sendMessage: (message: WebSocketMessage) => boolean;
  /** Clear current error */
  clearError: () => void;
  /** Force reconnection attempt */
  reconnect: () => void;
  /** Check if connection is healthy */
  isHealthy: () => boolean;
}

export interface UseWebSocketClientReturn {
  state: WebSocketState;
  actions: WebSocketActions;
}

/**
 * Hook for managing WebSocket connection to the backend server
 * Handles connection lifecycle, message sending/receiving, and reconnection logic
 * Requirements: 3.1, 9.1
 */
export const useWebSocketClient = (callbacks?: WebSocketCallbacks): UseWebSocketClientReturn => {
  //console.log('🔌 [WebSocket] Hook initialized');
  
  const [state, setState] = useState<WebSocketState>({
    connectionStatus: 'disconnected',
    isReady: false,
    error: null,
    lastStatus: null,
    sessionId: null,
  });

  const wsRef = useRef<WebSocket | null>(null);
  const urlRef = useRef<string | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const reconnectAttemptsRef = useRef<number>(0);
  const maxReconnectAttempts = 5;
  const baseReconnectDelay = 1000; // 1 second
  const isManualDisconnectRef = useRef<boolean>(false);
  const connectFnRef = useRef<((url: string) => void) | null>(null);
  const hasAttemptedConnectionRef = useRef<boolean>(false);

  // Calculate exponential backoff delay
  const getReconnectDelay = useCallback((attempt: number): number => {
    return Math.min(baseReconnectDelay * Math.pow(2, attempt), 30000); // Max 30 seconds
  }, []);



  // Store callbacks in a ref to avoid dependency issues
  const callbacksRef = useRef(callbacks);
  useEffect(() => {
    callbacksRef.current = callbacks;
  }, [callbacks]);

  // Update connection status and notify callback
  const updateConnectionStatus = useCallback((status: WebSocketState['connectionStatus']) => {
    setState((prev: WebSocketState) => {
      // Skip update if status hasn't changed
      if (prev.connectionStatus === status) {
        return prev;
      }
      
      return { ...prev, connectionStatus: status, isReady: status === 'connected' };
    });
    
    // Only notify callback after first connection attempt to avoid initial state noise
    if (hasAttemptedConnectionRef.current && callbacksRef.current?.onConnectionChange) {
      callbacksRef.current.onConnectionChange(status);
    }
  }, []); // No dependencies - uses refs instead

  // Handle incoming WebSocket messages
  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const message: WebSocketMessage = JSON.parse(event.data);
      
      switch (message.type) {
        case 'transcription':
          const transcriptionMsg = message as TranscriptionMessage;
          const result = transcriptionMsg.payload;
          
          // Validate transcription result structure
          if (!result || typeof result.text !== 'string' || typeof result.isPartial !== 'boolean') {
            console.error('Invalid transcription result format:', result);
            setState((prev: WebSocketState) => ({ ...prev, error: 'Received invalid transcription result format' }));
            return;
          }
          
          // Log transcription result for debugging
          console.log(`Received ${result.isPartial ? 'partial' : 'final'} transcription:`, result.text);
          
          callbacksRef.current?.onTranscription?.(result);
          break;
          
        case 'error':
          const errorMsg = message as ErrorWebSocketMessage;
          console.error('Received error from server:', errorMsg.payload);
          setState((prev: WebSocketState) => ({ ...prev, error: errorMsg.payload.message }));
          callbacksRef.current?.onError?.(errorMsg.payload);
          break;
          
        case 'status':
          const statusMsg = message as StatusMessage;
          console.log('Received status update:', statusMsg.payload);
          setState((prev: WebSocketState) => ({ 
            ...prev, 
            lastStatus: statusMsg.payload,
            sessionId: statusMsg.payload.sessionId || prev.sessionId
          }));
          callbacksRef.current?.onStatusUpdate?.(statusMsg.payload);
          break;
          
        default:
          console.warn('Unknown message type:', message.type);
          setState((prev: WebSocketState) => ({ ...prev, error: `Unknown message type received: ${message.type}` }));
      }
    } catch (error) {
      console.error('Failed to parse WebSocket message:', error);
      setState((prev: WebSocketState) => ({ ...prev, error: 'Failed to parse server message' }));
    }
  }, []); // No dependencies - uses refs instead

  // Handle WebSocket connection open
  const handleOpen = useCallback(() => {
    console.log('WebSocket connected');
    reconnectAttemptsRef.current = 0;
    updateConnectionStatus('connected');
    setState((prev: WebSocketState) => ({ ...prev, error: null }));
  }, [updateConnectionStatus]);

  // Handle WebSocket connection close
  const handleClose = useCallback((event: CloseEvent) => {
    console.log('WebSocket closed:', event.code, event.reason);
    wsRef.current = null;
    
    if (event.code === 1000 || isManualDisconnectRef.current) {
      // Normal closure or manual disconnect
      updateConnectionStatus('disconnected');
      isManualDisconnectRef.current = false;
    } else {
      // Abnormal closure - analyze error and determine recovery strategy
      updateConnectionStatus('error');
      
      const errorInfo = WebSocketErrorHandler.analyzeCloseEvent(event);
      const displayMessage = WebSocketErrorHandler.getDisplayMessage(errorInfo);
      
      setState((prev: WebSocketState) => ({ ...prev, error: displayMessage }));
      
      // Determine if we should attempt reconnection
      const shouldReconnect = WebSocketErrorHandler.shouldAutoReconnect(
        errorInfo, 
        reconnectAttemptsRef.current, 
        maxReconnectAttempts
      );
      
      if (shouldReconnect && urlRef.current && !isManualDisconnectRef.current) {
        const delay = WebSocketErrorHandler.calculateRetryDelay(errorInfo, reconnectAttemptsRef.current);
        console.log(`Attempting reconnection in ${delay}ms (attempt ${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts})`);
        
        setState((prev: WebSocketState) => ({ 
          ...prev, 
          error: `${displayMessage} Reconnecting in ${Math.ceil(delay / 1000)} seconds... (${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts})` 
        }));
        
        reconnectTimeoutRef.current = window.setTimeout(() => {
          reconnectAttemptsRef.current++;
          if (connectFnRef.current && urlRef.current) {
            connectFnRef.current(urlRef.current);
          }
        }, delay);
      } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
        setState((prev: WebSocketState) => ({ 
          ...prev, 
          error: 'Connection lost and maximum reconnection attempts exceeded. Please check your network connection and try again.'
        }));
      }
    }
  }, [updateConnectionStatus]);

  // Handle WebSocket errors
  const handleError = useCallback((event: Event) => {
    console.error('WebSocket error:', event);
    updateConnectionStatus('error');
    
    const errorInfo = WebSocketErrorHandler.analyzeError(event);
    const displayMessage = WebSocketErrorHandler.getDisplayMessage(errorInfo);
    
    setState((prev: WebSocketState) => ({ ...prev, error: displayMessage }));
  }, [updateConnectionStatus]);

  // Connect to WebSocket server
  const connect = useCallback((url: string): void => {
    console.log('🔌 [WebSocket] Attempting to connect to:', url);
    console.log('🔌 [WebSocket] Current connection state:', wsRef.current?.readyState);
    
    // Mark that we've attempted a connection
    hasAttemptedConnectionRef.current = true;
    
    // Prevent multiple simultaneous connection attempts
    if (wsRef.current && (wsRef.current.readyState === WebSocket.CONNECTING || wsRef.current.readyState === WebSocket.OPEN)) {
      console.log('🔌 [WebSocket] Connection already exists or in progress, skipping');
      return;
    }
    
    // Clear any existing reconnection timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Close existing connection
    if (wsRef.current) {
      console.log('🔌 [WebSocket] Closing existing connection');
      wsRef.current.close();
      wsRef.current = null;
    }

    urlRef.current = url;
    updateConnectionStatus('connecting');
    setState((prev: WebSocketState) => ({ ...prev, error: null }));

    try {
      console.log('🔌 [WebSocket] Creating WebSocket connection...');
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.addEventListener('open', handleOpen);
      ws.addEventListener('message', handleMessage);
      ws.addEventListener('close', handleClose);
      ws.addEventListener('error', handleError);
      
      console.log('🔌 [WebSocket] Event listeners attached');
    } catch (error) {
      console.error('❌ [WebSocket] Failed to create WebSocket connection:', error);
      updateConnectionStatus('error');
      setState((prev: WebSocketState) => ({ ...prev, error: 'Failed to create WebSocket connection' }));
    }
  }, [updateConnectionStatus, handleOpen, handleMessage, handleClose, handleError]);

  // Store connect function in ref to avoid circular dependency
  connectFnRef.current = connect;

  // Disconnect from WebSocket server
  const disconnect = useCallback(() => {
    // Mark as manual disconnect to prevent reconnection
    isManualDisconnectRef.current = true;
    
    // Clear reconnection timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Reset reconnection attempts
    reconnectAttemptsRef.current = 0;
    urlRef.current = null;

    // Close WebSocket connection
    if (wsRef.current) {
      wsRef.current.close(1000, 'User initiated disconnect');
      wsRef.current = null;
    }

    updateConnectionStatus('disconnected');
    setState((prev: WebSocketState) => ({ ...prev, error: null, lastStatus: null, sessionId: null }));
  }, [updateConnectionStatus]);

  // Send a message to the server
  const sendMessage = useCallback((message: WebSocketMessage): boolean => {
    console.log('📨 [WebSocket] sendMessage called, type:', message.type);
    console.log('📨 [WebSocket] WebSocket ref exists:', !!wsRef.current);
    console.log('📨 [WebSocket] WebSocket readyState:', wsRef.current?.readyState, '(OPEN=1)');
    
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.warn('⚠️ [WebSocket] WebSocket not connected, cannot send message');
      setState((prev: WebSocketState) => ({ ...prev, error: 'Cannot send message: WebSocket not connected' }));
      return false;
    }

    try {
      const messageStr = JSON.stringify(message);
      console.log('📤 [WebSocket] Sending message, length:', messageStr.length);
      wsRef.current.send(messageStr);
      console.log('✅ [WebSocket] Message sent successfully');
      return true;
    } catch (error) {
      console.error('❌ [WebSocket] Failed to send WebSocket message:', error);
      setState((prev: WebSocketState) => ({ ...prev, error: 'Failed to send message to server' }));
      return false;
    }
  }, []);

  // Send audio chunk to server
  const sendAudioChunk = useCallback((chunk: AudioChunk): boolean => {
    console.log('🔊 [WebSocket] sendAudioChunk called, chunk size:', chunk.data.byteLength);
    console.log('🔊 [WebSocket] WebSocket state:', wsRef.current?.readyState);
    
    // Validate audio chunk before encoding
    const validation = AudioEncoder.validateAudioChunk(chunk);
    if (!validation.valid) {
      console.error('❌ [WebSocket] Invalid audio chunk:', validation.error);
      setState((prev: WebSocketState) => ({ ...prev, error: `Audio encoding error: ${validation.error}` }));
      return false;
    }
    console.log('✅ [WebSocket] Audio chunk validation passed');

    try {
      // Encode audio chunk for transmission
      console.log('🔄 [WebSocket] Encoding audio chunk...');
      const encodedChunk = AudioEncoder.encodeAudioChunk(chunk);
      console.log('✅ [WebSocket] Audio chunk encoded, data length:', encodedChunk.data?.byteLength || 0);
      
      const message: AudioMessage = {
        type: 'audio',
        payload: encodedChunk,
        messageId: crypto.randomUUID(),
      };
      
      console.log('📤 [WebSocket] Sending audio message to server...');
      const result = sendMessage(message);
      console.log(`${result ? '✅' : '❌'} [WebSocket] Audio message send result:`, result);
      return result;
    } catch (error) {
      console.error('❌ [WebSocket] Failed to encode audio chunk:', error);
      setState((prev: WebSocketState) => ({ ...prev, error: 'Failed to encode audio data for transmission' }));
      return false;
    }
  }, [sendMessage]);

  // Send session configuration
  const sendConfig = useCallback((
    language: LanguageOption, 
    audioFormat: string = 'pcm', 
    sampleRate: number = 16000
  ): boolean => {
    const message = createConfigMessage(language, audioFormat, sampleRate);
    return sendMessage(message);
  }, [sendMessage]);

  // Clear current error
  const clearError = useCallback(() => {
    setState((prev: WebSocketState) => ({ ...prev, error: null }));
  }, []);

  // Force reconnection attempt
  const reconnect = useCallback(() => {
    if (urlRef.current && connectFnRef.current) {
      console.log('Manual reconnection requested');
      reconnectAttemptsRef.current = 0; // Reset attempts for manual reconnection
      connectFnRef.current(urlRef.current);
    } else {
      setState((prev: WebSocketState) => ({ ...prev, error: 'No URL available for reconnection' }));
    }
  }, []);

  // Check if connection is healthy
  const isHealthy = useCallback((): boolean => {
    return wsRef.current?.readyState === WebSocket.OPEN;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, []); // Empty deps - only run on unmount

  const actions = useMemo(() => ({
    connect,
    disconnect,
    sendAudioChunk,
    sendConfig,
    sendMessage,
    clearError,
    reconnect,
    isHealthy,
  }), [connect, disconnect, sendAudioChunk, sendConfig, sendMessage, clearError, reconnect, isHealthy]);

  return {
    state,
    actions,
  };
};